import { useState, useEffect, useRef } from "react";
import { suggestSignals } from "../lib/series/suggestSignals.js";

/**
 * Agentic signal suggestions for the active forecasting question — fetches
 * POST /api/signals/suggest (server-gated by the signal-panel-suggestions flag)
 * and returns the suggested signals for the drawer to surface, plus the
 * auto-select subset + flag for the auto-plot path (useAutoSelectSignals).
 *
 * Everything is empty/false whenever the flags are off, the signals-service
 * isn't configured, or anything fails (suggestSignals never throws), so the
 * drawer just shows no suggestion section — invisible when disabled. Re-fetches
 * when the question changes, aborting any in-flight request first (arm the
 * controller before the await) so a stale response can't clobber a newer
 * question's result.
 *
 * `resultQuestion` is the question the current `autoSelect`/`autoSelectEnabled`
 * belong to — it lags `question` until the fetch resolves, so the auto-plot hook
 * can tell "results not in yet" from "results are in and the agent picked
 * nothing" (both have an empty `autoSelect`). That distinction is what lets it
 * clear a prior question's auto-plot on a new plot-nothing question.
 *
 * `loading` is true while a question's fetch is in flight (results not yet in for
 * the current question) — the drawer uses it to show the loading skeleton. Whether
 * the suggestion section is reserved at all is a SEPARATE, boot-time decision
 * (`/api/me`'s `signalSuggestionsEnabled`, via useAuthSession) — not tracked here —
 * so the section can mount before this hook's first fetch resolves.
 *
 * @param {{ question: string|undefined }} args
 * @returns {{ suggestions, autoSelect, autoSelectEnabled: boolean,
 *            resultQuestion: string|null, loading: boolean }}
 */
export function useSignalSuggestions({ question }) {
  const [suggestions, setSuggestions] = useState([]);
  const [autoSelect, setAutoSelect] = useState([]);
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(false);
  const [resultQuestion, setResultQuestion] = useState(null);
  const abortRef = useRef(null);
  useEffect(() => {
    const q = (question || "").trim();
    // Cancel any previous request before we start (or clear) — newest wins.
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl; // arm before the await
    if (!q) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuggestions([]);
      setAutoSelect([]);
      setResultQuestion("");
      return () => ctrl.abort();
    }
    // Clear the auto-select subset before refetching: it drives a side-effect
    // (auto-plot), so a previous question's value must never leak into this one.
    // Suggestions (chips) are left as-is to avoid a visible flicker on refetch.
    // resultQuestion is deliberately NOT cleared here — it keeps pointing at the
    // prior question until THIS fetch resolves, so the hook knows results for `q`
    // haven't arrived yet.
    setAutoSelect([]);
    suggestSignals(q, { signal: ctrl.signal }).then((res) => {
      if (ctrl.signal.aborted) return;
      setSuggestions(Array.isArray(res?.suggestions) ? res.suggestions : []);
      setAutoSelect(Array.isArray(res?.autoSelect) ? res.autoSelect : []);
      setAutoSelectEnabled(res?.autoSelectEnabled === true);
      setResultQuestion(q);
    });
    return () => ctrl.abort();
  }, [question]);
  const q = (question || "").trim();
  // In flight iff there's a question whose results haven't landed yet.
  const loading = !!q && resultQuestion !== q;
  return {
    suggestions,
    autoSelect,
    autoSelectEnabled,
    resultQuestion,
    loading,
  };
}
